import { css } from '@emotion/react'
import { memo, ReactElement } from 'react'
import { routerPush } from '../../../../utilities/router'
import { LoadingData } from '../../../common/LoadingData'
import { useLayoutSelector } from '../../../Hooks/useLayout'
import Link from '../../Link'

const emptyStyles = css({
    justifyContent: 'center',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    display: 'flex',
    width: '100%',
    color: '#999',
    fontSize: 12,
    padding: 24,
})
const emptyHeaderStyles = css({
    fontSize: '1.1607142857142858rem',
    fontWeight: 600,
    marginBottom: 4,
})
const innerStyleOverrides = {
    color: '#999',
    fontSize: 12,
    marginTop: 4,
}
const exhaustedStyles = css({
    justifyContent: 'center',
    alignItems: 'center',
    display: 'flex',
    width: '100%',
    color: '#999',
    fontSize: 12,
    padding: 8,
})
const rootOuterStyleOverrides = {
    paddingBottom: 4,
}
const rootInnerStyleOverrides = {
    width: '100%',
    padding: 0,
}
const noteOuterStyleOverrides = {
    position: 'relative' as const,
    width: '100%',
    marginTop: 4,
    height: 8,
}
const noteInnerStyleOverrides = {
    position: 'absolute' as const,
    color: '#999',
    fontSize: 12,
    width: '100%',
}

type PMStatusProps = {
    EmptyAction?: ReactElement | string
    isExhausted: boolean
    isEmpty: boolean
    noVisibileUnread?: boolean
    showUnreadOnly?: boolean
}

const onExplore = () => {
    routerPush('/search')
}

const DefaultEmptyAction = (
    <Link page="search" text mfc onClick={onExplore}>
        explore
    </Link>
)

const PMStatus = memo((props: PMStatusProps) => {
    const fullWindow = useLayoutSelector('fullWindow')

    const {
        EmptyAction = DefaultEmptyAction,
        isExhausted,
        isEmpty,
        noVisibileUnread,
        showUnreadOnly,
    } = props

    if (noVisibileUnread) {
        return (
            <div
                key="pm-user-list-no-unread"
                data-testid="PrivateMessages__NoUnread__Notice__Container"
                css={exhaustedStyles}
            >
                You do not have any unread messages.
            </div>
        )
    }

    if (showUnreadOnly) {
        return (
            <div
                key="pm-user-list-showing-unread"
                data-testid="PrivateMessages__ShowingUnread__Notice__Container"
                css={exhaustedStyles}
            />
        )
    }

    if (isEmpty && isExhausted) {
        return (
            <div
                key="pm-user-list-notice-empty"
                data-testid="PrivateMessages__Empty__Notice__Container"
                css={emptyStyles}
            >
                <div css={emptyHeaderStyles}>Private Messages</div>
                We couldn&apos;t locate any private message history.
                <br />
                <div>Feel free to {EmptyAction} and make history!</div>
            </div>
        )
    }

    if (isEmpty && !isExhausted) {
        return (
            <LoadingData
                noteInnerStyleOverrides={innerStyleOverrides}
                note={'Trying to locate your most recent history...'}
                key="pm-user-list-loading-data-indicator-initial"
                text="Private Messages"
                active
            />
        )
    }

    if (isExhausted) {
        const text = !fullWindow ? (
            <>This is all of the history we have for now! &#127881;</>
        ) : (
            ''
        )

        return (
            <div key="pm-user-list-notice-exhausted-data" css={exhaustedStyles}>
                {text}
            </div>
        )
    }

    return (
        <LoadingData
            key="pm-user-list-loading-data-indicator-subsequent"
            note={'Loading more private message history...'}
            rootOuterStyleOverrides={rootOuterStyleOverrides}
            rootInnerStyleOverrides={rootInnerStyleOverrides}
            noteOuterStyleOverrides={noteOuterStyleOverrides}
            noteInnerStyleOverrides={noteInnerStyleOverrides}
            text=""
            active
        />
    )
})

export default PMStatus
